import { useMemo } from "react";
import Nestable from "react-nestable";
import { bulletIcon, gripHandle } from "./menuTreeHandles";
import useDragAutoScroll from "./useDragAutoScroll";
import useStableNestable from "./useStableNestable";

/**
 * Flat, reorderable list of product rows (drag the grip to change the
 * order shown to customers).
 * items: [{ id, title }]
 * onReorder(orderedIds) · onRemove(item)
 */
const MenuTreeProductList = ({ items, onReorder, onRemove, dragHint, removeHint }) => {
  // Nestable resets an in-progress drag whenever a prop changes by reference,
  // so keep the items array and the handle element stable between renders.
  const rows = useMemo(() => (items || []).map((p) => ({ id: p.id, title: p.title })), [items]);
  const handle = useMemo(() => gripHandle(dragHint), [dragHint]);
  const autoScroll = useDragAutoScroll();
  const nestable = useStableNestable({
    renderItem: ({ item, handler }) => (
      <div className="menu-tree__product">
        {handler}
        {bulletIcon()}
        <span className="menu-tree__product-label" title={item.title}>
          {item.title}
        </span>
        {onRemove && (
          <button
            type="button"
            className="btn menu-tree__remove"
            title={removeHint}
            aria-label={removeHint}
            onClick={(e) => {
              e.stopPropagation();
              onRemove(item);
            }}
          >
            <i className="fas fa-times" />
          </button>
        )}
      </div>
    ),
    onChange: ({ items: next }) => {
      if (!Array.isArray(next)) return;
      const orderedIds = next.map((row) => row.id);
      const unchanged = orderedIds.every((id, index) => rows[index]?.id === id);
      if (!unchanged) onReorder(orderedIds);
    },
  });

  if (rows.length === 0) return null;

  return (
    <div ref={autoScroll.rootRef}>
    <Nestable
      group={nestable.group}
      items={rows}
      maxDepth={1}
      handler={handle}
      onDragStart={autoScroll.onDragStart}
      onDragEnd={autoScroll.onDragEnd}
      renderItem={nestable.renderItem}
      onChange={nestable.onChange}
    />
    </div>
  );
};

export default MenuTreeProductList;
